import React from "react";
import {
	addTimeToDate,
	Button,
	CalendarTypes,
	ClassNameProps,
	Column,
	compareObjects,
	compareStringsAsc,
	dateDiff,
	FormattedText,
	getObjectKeys,
	Grid,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	stringToLocalDate,
	StyleProps,
	Timestamp,
	toLocalDateString,
	Tooltip,
	TooltipButton,
	TruncatedText,
} from "@hex-insights/core";
import { EventCalendar } from "../../../../../../../Components/EventCalendar";
import {
	datedItemsToDateMap,
	dateToDateMapKey,
	rangeDatedItemsToDateMap,
} from "../../../../../../../Components/EventCalendar/utils";
import { Icon } from "../../../../../../../Components/Icon";
import { Tile } from "../../../../../../../Components/Tile";
import { Timeline } from "../../../../../../../Components/Timeline";
import {
	Student,
	StudentAttendanceCalendarQuery,
	StudentNameAndImageFragment,
	TimelineFilterInput,
	TimelineObjectType,
	useStudentAttendanceCalendarQuery,
	useStudentSchoolAttendanceRecordCountStatisticsRangeSumQuery,
} from "../../../../../../../Utilities";
import { SchoolAttendanceRecordFormat } from "../../../../../../Format";
import { StudentNoteTypeFormValues } from "../../../../../../FormUtilities";
import { AcademicTermUtilities } from "../../../../../../Utilities";
import { AttendanceRatingBadge } from "../../../../../_General";
import * as AttendanceStatusComponents from "../../../../../AttendanceStatus";
import { SchoolAttendanceRecordStudentDateModal } from "../../../../../SchoolAttendanceRecord";
import { SchoolAttendanceRecordReportSummaryView } from "../../../../../SchoolAttendanceRecord/Reports/Tile";
import * as StudentNoteComponents from "../../../../../StudentNote";
import { attendanceNoteToCalendarEvent, attendanceRecordToCalendarEvent } from "./utils";
import styles from "./styles.module.css";

export type StudentAttendanceTileProps = {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
};

export function StudentAttendanceTile({ student }: StudentAttendanceTileProps) {
	const [daysBack, setDaysBack] = React.useState(7);

	const { data } = useStudentSchoolAttendanceRecordCountStatisticsRangeSumQuery({
		variables: {
			studentID: student.id,
			startDate: toLocalDateString(addTimeToDate(new Date(), [-1 * daysBack + 1, "days"])),
			endDate: toLocalDateString(addTimeToDate(new Date(), [1, "day"])),
		},
	});

	const { currentAcademicTerm } = AcademicTermUtilities.useAcademicTermsNavigation();
	const daysIntoCurrentAcademicTerm =
		dateDiff(
			new Date(),
			currentAcademicTerm ? stringToLocalDate(currentAcademicTerm.startDate, "day") : new Date(),
			"days",
		) + 1;

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Tile>
				<Tile.Header>
					<Row justify="space-between" align="center">
						<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
							<Heading.H3 noMargin>Attendance</Heading.H3>

							<TooltipButton side="bottom">
								{({ close }) => (
									<Tooltip isInteractable>
										<Tooltip.Body>
											<Column justify="spaced-start" verticalSpacing="0.25rem">
												<Button
													variant="link"
													size="small"
													onClick={() => {
														setDaysBack(7);
														close();
													}}
													className="nowrap"
													style={{ width: "100%" }}
												>
													Past Week
												</Button>
												<Button
													variant="link"
													size="small"
													onClick={() => {
														setDaysBack(30);
														close();
													}}
													className="nowrap"
													style={{ width: "100%" }}
												>
													Past Month
												</Button>
												<Button
													variant="link"
													size="small"
													onClick={() => {
														setDaysBack(daysIntoCurrentAcademicTerm);
														close();
													}}
													className="nowrap"
													style={{ width: "100%" }}
												>
													This Term
												</Button>
											</Column>
										</Tooltip.Body>
									</Tooltip>
								)}
								{({ onClick }) => (
									<Button variant="link" size="small" onClick={onClick} className="button--icon">
										<Icon.CalendarBlank size={14} className="block" />
									</Button>
								)}
							</TooltipButton>
						</Row>

						<Button variant="link" size="small" onClick={openModal}>
							View
						</Button>
					</Row>
				</Tile.Header>
				<Tile.Body>
					<SchoolAttendanceRecordReportSummaryView
						statistics={data?.studentSchoolAttendanceRecordCountStatisticsRangeSum}
						chartCenterValue={daysBack}
						chartCenterLabel="Days"
					/>
				</Tile.Body>
			</Tile>

			<Modal.If condition={isModalOpen}>
				<StudentAttendanceModal student={student} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

export type StudentAttendanceModalProps = Pick<ModalProps, "ifRef" | "onClose" | "className" | "style"> & {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
};

export function StudentAttendanceModal({ student, ifRef, onClose, className, style }: StudentAttendanceModalProps) {
	return (
		<Modal size="large" ifRef={ifRef} onClose={onClose} className={className} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Attendance</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Row justify="spaced-start" horizontalSpacing="1rem" style={{ height: "100%" }}>
					<div
						style={{
							flexShrink: 0,
							width: "20rem",
							height: "fit-content",
							position: "sticky",
							top: "4rem",
							zIndex: 10,
						}}
					>
						<StudentAttendanceCalendar student={student} />
					</div>

					<Column justify="spaced-start" verticalSpacing="1.5rem" style={{ flexGrow: 1 }}>
						<StatisticsSection studentID={student.id} />

						<TimelineSection studentID={student.id} />
					</Column>
				</Row>
			</Modal.Body>
		</Modal>
	);
}

export type StudentAttendanceCalendarProps = {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
} & Partial<ClassNameProps & StyleProps>;

export function StudentAttendanceCalendar({ student, className, style }: StudentAttendanceCalendarProps) {
	const { value, firstOfMonth, lastOfMonth, onActiveStartDateChange, onClickMonth, onChange } =
		EventCalendar.useDates();

	const { data } = useStudentAttendanceCalendarQuery({
		variables: {
			studentID: student.id,
			startDate: toLocalDateString(firstOfMonth),
			endDate: toLocalDateString(lastOfMonth),
		},
	});

	const { attendanceDateMap, noteDateMap } = React.useMemo(() => {
		if (!data) {
			return { attendanceDateMap: {}, noteDateMap: {} };
		}
		const attendanceDateMap = datedItemsToDateMap(data.schoolAttendanceRecordConnection.edges, "attendanceDate", true);
		const attendanceDateMapKeys = getObjectKeys(attendanceDateMap);
		for (let i = 0; i < attendanceDateMapKeys.length; i++) {
			const records = attendanceDateMap[attendanceDateMapKeys[i]];
			records.sort(compareObjects("arrivalTime", compareStringsAsc()));
		}

		const notes = data.studentNoteConnection.edges.map((e) => ({
			...e.node,
			startDate: e.node.studentAttendanceNote!.startDate,
			endDate: e.node.studentAttendanceNote!.endDate,
		}));
		const noteDateMap = rangeDatedItemsToDateMap(notes, "startDate", "endDate", true);

		return { attendanceDateMap, noteDateMap };
	}, [data]);

	const dateKey = dateToDateMapKey(value);
	const attendanceOnSelectedDate = attendanceDateMap[dateKey] ?? [];
	const notesOnSelectedDate = noteDateMap[dateKey] ?? [];

	const tileContent = React.useCallback<CalendarTypes.TileContentFunc>(
		({ date, view }) => {
			if (view !== "month") {
				return null;
			}
			const dateKey = dateToDateMapKey(date);
			const attendanceEventsOnSelectedDate =
				attendanceDateMap[dateKey]?.map((e) => attendanceRecordToCalendarEvent(e)) ?? [];
			const noteEventsOnSelectedDate = noteDateMap[dateKey]?.map((e) => attendanceNoteToCalendarEvent(e)) ?? [];
			const events = [...attendanceEventsOnSelectedDate, ...noteEventsOnSelectedDate];
			return <EventCalendar.TileContent events={events} />;
		},
		[attendanceDateMap, noteDateMap],
	);

	return (
		<Column justify="spaced-start" className={className} style={style}>
			<EventCalendar.Calendar
				value={value}
				onActiveStartDateChange={onActiveStartDateChange}
				onClickMonth={onClickMonth}
				onChange={onChange}
				noSecondaryNavigation
				minDetail="month"
				tileContent={tileContent}
			/>

			<Column justify="spaced-start" verticalSpacing="1rem">
				<strong>
					{value.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", weekday: "short" })}
				</strong>
				{attendanceOnSelectedDate.map((record) => (
					<CalendarSchoolAttendanceItem key={record.id} student={student} attendanceRecord={record} />
				))}
				<If condition={attendanceOnSelectedDate.length === 0}>
					<CalendarSchoolAttendanceNotTaken student={student} date={toLocalDateString(value)} />
				</If>
				<Column justify="spaced-start" verticalSpacing="0.5rem">
					<span className={styles["calendar__notes__label"]}>Notes</span>

					<If condition={notesOnSelectedDate.length > 0}>
						<Column justify="spaced-start" verticalSpacing="0.25rem">
							{notesOnSelectedDate.map((note) => (
								<AttendanceNoteListItem key={note.id} note={note} style={{ width: "100%" }} />
							))}
						</Column>
					</If>

					<AttendanceNoteCreateController student={student} date={toLocalDateString(value)} />
				</Column>
			</Column>
		</Column>
	);
}

type CalendarSchoolAttendanceItemProps = {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
	attendanceRecord: StudentAttendanceCalendarQuery["schoolAttendanceRecordConnection"]["edges"][0]["node"];
};

function CalendarSchoolAttendanceItem({ student, attendanceRecord }: CalendarSchoolAttendanceItemProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const attendanceRecordWithStudent = React.useMemo(
		() => ({
			...attendanceRecord,
			student,
		}),
		[attendanceRecord, student],
	);

	return (
		<React.Fragment>
			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Row justify="space-between" align="center">
					<Tooltip.Container style={{ flexGrow: 1 }}>
						<Tooltip className="tooltip--inverted">
							<Tooltip.Body>
								<span className="nowrap">Click to edit</span>
							</Tooltip.Body>
						</Tooltip>

						<Button onClick={openModal} style={{ width: "100%", padding: 0, border: "none" }}>
							<AttendanceStatusComponents.AttendanceStatusBadge
								attendanceStatus={attendanceRecord.attendanceStatus}
								extended
								style={{ fontSize: "0.875rem" }}
							/>
						</Button>
					</Tooltip.Container>

					<If condition={!!attendanceRecord.notes}>
						<Tooltip.Container style={{ marginLeft: "0.75rem" }}>
							<Tooltip className="tooltip--big" style={{ fontSize: "0.875rem" }}>
								<Tooltip.Header>
									<strong>Attendance Note</strong>
								</Tooltip.Header>
								<Tooltip.Body style={{ width: "20rem" }}>
									<FormattedText>{attendanceRecord.notes}</FormattedText>
								</Tooltip.Body>
							</Tooltip>

							<Icon.NotePencil size={14} className="block" />
						</Tooltip.Container>
					</If>
				</Row>

				<Row justify="spaced-start">
					{!!attendanceRecord.arrivalTime && (
						<span className={styles["calendar__attendance-record__timestamp"]}>
							Check In:{" "}
							<Timestamp value={attendanceRecord.arrivalTime}>
								<strong>
									{SchoolAttendanceRecordFormat.Fields.arrivalTime(attendanceRecord.arrivalTime, { noDate: true })}
								</strong>
							</Timestamp>
						</span>
					)}
					{!!attendanceRecord.departureTime && (
						<span className={styles["calendar__attendance-record__timestamp"]}>
							Check Out:{" "}
							<Timestamp value={attendanceRecord.departureTime}>
								<strong>
									{SchoolAttendanceRecordFormat.Fields.departureTime(attendanceRecord.departureTime, { noDate: true })}
								</strong>
							</Timestamp>
						</span>
					)}
				</Row>
			</Column>

			<Modal.If condition={isModalOpen}>
				<SchoolAttendanceRecordStudentDateModal
					student={student}
					date={attendanceRecord.attendanceDate}
					schoolAttendanceRecord={attendanceRecordWithStudent}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type CalendarSchoolAttendanceNotTakenProps = {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
	date: string;
};

function CalendarSchoolAttendanceNotTaken({ student, date }: CalendarSchoolAttendanceNotTakenProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Column justify="spaced-start" align="center">
				<div className={styles["calendar__attendance-record--none"]} style={{ width: "100%" }}>
					No Attendance Taken
				</div>

				<Button variant="link" size="small" onClick={openModal}>
					Take Attendance
				</Button>
			</Column>

			<Modal.If condition={isModalOpen}>
				<SchoolAttendanceRecordStudentDateModal
					student={student}
					date={date}
					schoolAttendanceRecord={null}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type AttendanceNoteListItemProps = {
	note: StudentAttendanceCalendarQuery["studentNoteConnection"]["edges"][0]["node"];
} & Partial<ClassNameProps & StyleProps>;

function AttendanceNoteListItem({ note, className, style }: AttendanceNoteListItemProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(styles["attendance-note-button"], className)}
				style={
					{ "--note-color": StudentNoteComponents.studentNoteTypeColors.attendance, ...style } as React.CSSProperties
				}
			>
				<Row
					justify="spaced-start"
					align="center"
					horizontalSpacing="0.5rem"
					className={styles["attendance-note-button__content"]}
				>
					<Icon.NotePencil size={14} color={StudentNoteComponents.studentNoteTypeColors.attendance} />
					<TruncatedText>{note.body}</TruncatedText>
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentNoteComponents.ControlledStudentNoteModal
					studentNoteID={note.id}
					noteType={note.noteType}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type AttendanceNoteCreateControllerProps = {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
	date: string;
};

function AttendanceNoteCreateController({ student, date }: AttendanceNoteCreateControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const formStateArgs = React.useMemo(
		() => ({
			initialFormValues: {
				primaryStudentIDs: [student.id],
				studentAttendanceNote: [
					{
						...StudentNoteTypeFormValues.initialAttendanceCreate.studentAttendanceNote[0],
						startDate: date,
						endDate: date,
					},
				],
			},
		}),
		[student.id, date],
	);

	return (
		<React.Fragment>
			<Row justify="center">
				<Button variant="link" size="small" onClick={openModal}>
					Write Attendance Note
				</Button>
			</Row>

			<Modal.If condition={isModalOpen}>
				<StudentNoteComponents.StudentAttendanceNoteCreateModal formStateArgs={formStateArgs} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

type StatisticsSectionProps = {
	studentID: Student["id"];
} & Partial<ClassNameProps & StyleProps>;

function StatisticsSection({ studentID, className, style }: StatisticsSectionProps) {
	const { currentAcademicTerm } = AcademicTermUtilities.useAcademicTermsNavigation();

	const { data } = useStudentSchoolAttendanceRecordCountStatisticsRangeSumQuery({
		variables: {
			startDate: currentAcademicTerm?.startDate ?? toLocalDateString(new Date()),
			endDate: currentAcademicTerm?.endDate ?? toLocalDateString(new Date()),
			studentID,
		},
		fetchPolicy: "cache-and-network",
		skip: !currentAcademicTerm,
	});
	const statistics = data?.studentSchoolAttendanceRecordCountStatisticsRangeSum;

	return (
		<Grid tracks={3} gap="0.75rem" className={className} style={style}>
			<Column justify="spaced-start" align="center" verticalSpacing="0.25rem" className={styles["tile__statistic"]}>
				<span className={styles["tile__statistic__label"]}>Overall Rating</span>
				<AttendanceRatingBadge rating="good" />
			</Column>
			<Column justify="spaced-start" align="center" verticalSpacing="0.25rem" className={styles["tile__statistic"]}>
				<span className={styles["tile__statistic__label"]}>Absent</span>
				<span className={styles["tile__statistic__value"]}>{statistics?.absentTotal ?? 0}</span>
			</Column>
			<Column justify="spaced-start" align="center" verticalSpacing="0.25rem" className={styles["tile__statistic"]}>
				<span className={styles["tile__statistic__label"]}>Present</span>
				<span className={styles["tile__statistic__value"]}>{statistics?.presentTotal ?? 0}</span>
			</Column>
		</Grid>
	);
}

type TimelineSectionProps = {
	studentID: Student["id"];
} & Partial<ClassNameProps & StyleProps>;

function TimelineSection({ studentID, className, style }: TimelineSectionProps) {
	const filter = React.useMemo<TimelineFilterInput>(
		() => ({
			objectTypeIn: [
				TimelineObjectType.SchoolAttendanceRecord,
				TimelineObjectType.StudentNote,
				TimelineObjectType.StudentAttendanceNote,
			],
			studentID: studentID,
		}),
		[studentID],
	);

	return <Timeline filter={filter} pageSize={10} className={className} style={{ height: "100%", ...style }} />;
}
